import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Button,
  Snackbar,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  fetchUserDetails,
  fetchUserPermissions,
} from "../../../redux/Slice/User/userDetailsSlice";
import { updateTeamMemberById } from "../../../redux/Slice/Settings/updatefunctionsSlice";
import { toast } from "react-hot-toast";
import { checkBeforeOutbound } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";

const Profile = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 0-599px
 // 1536px and above

  // Extract user details and loading state from Redux
  const { userDetails, isLoading } = useSelector((state) => state.userDetails);
  const { loading: updateLoading } = useSelector(
    (state) => state.updateFunctions
  );

  // Get edit profile permission
  const editProfilePermission = useSelector(
    (state) =>
      state.userDetails.userPermissions?.permissions?.Profile?.Edit ||
      state.userDetails.userPermissions?.permissions?.EditProfile
  );

  // State to handle the name change
  const [editedName, setEditedName] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  useEffect(() => {
    dispatch(fetchUserDetails());
    dispatch(fetchUserPermissions());
  }, [dispatch]);

  // Update state when userDetails change
  useEffect(() => {
    if (userDetails?.name) {
      setEditedName(userDetails.name);
    }
  }, [userDetails]);

  // Handle name change
  const handleNameChange = (e) => {
    setEditedName(e.target.value);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle save action to update the name
  const handleSave = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    const trimmedName = editedName.trim();
    const originalName = userDetails?.name?.trim();

    if (userDetails?._id && trimmedName !== originalName) {
      dispatch(
        updateTeamMemberById({
          id: userDetails._id,
          teamMemberData: { name: trimmedName },
        })
      )
        .unwrap()
        .then(() => {
          setSnackbar({
            open: true,
            message: "Name updated successfully!",
            severity: "success",
          });
          dispatch(fetchUserDetails({ force: true })); // Refresh user details (bypass cache — must reflect the save just made)
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message:
              error.message || "Failed to update name. Please try again.",
            severity: "error",
          });
        });
    }
  };

  const textFieldStyles = (theme) => ({
    "& .MuiInputLabel-root": {
      color: "text.secondary",
    },
    "& .MuiInputBase-input": {
      color: "text.primary",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.87)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "primary.main",
      },
    },
  });

  return (
    <Box
      sx={{
        padding: "20px",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        borderRadius: "8px",
        backgroundColor: "background.paper",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        display: "flex",
        flexDirection: "column",
        color: "text.primary",
        paddingBottom: { xs: "16px", sm: "16px", md: "40px", lg: "40px", xl: "20px" },
        height: { xs: "100%", sm: "100%", md: "auto", lg: "auto", xl: "calc(100vh - 104px)" },
        minHeight: { xs: 0, sm: 0, md: "auto", lg: "auto", xl: "calc(100vh - 104px)" },
        flex: 1,
        overflow: { xs: "hidden", sm: "hidden", md: "visible", lg: "visible", xl: "visible" },
      }}
    >
      {/* Hide Profile heading on xs screens since it's already shown in the header */}
      {!isXs && (
        <Typography 
          variant="h5" 
          sx={{ 
            marginBottom: { xs: "1rem", sm: "2rem" },
            fontSize: { xs: "16px", sm: "20px", md: "24px" },
          }}
        >
          Profile
        </Typography>
      )}

      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Box 
          sx={{ 
            display: "flex", 
            flexDirection: "column", 
            gap: "1.5rem",
            // Room above first Outlined TextField so the shrunk label is not clipped by overflow-y: auto
            paddingTop: { xs: "14px", sm: "14px", md: "10px", lg: "10px", xl: "10px" },
            height: { xs: "100%", sm: "100%", md: "auto", lg: "auto", xl: "100%" },
            minHeight: { xs: 0, sm: 0, md: "auto", lg: "auto", xl: 0 },
            flex: { xs: 1, sm: 1, md: "0 1 auto", lg: "0 1 auto", xl: "1 1 auto" },
            overflowY: { xs: "auto", sm: "auto", md: "visible", lg: "visible", xl: "visible" },
            overflowX: "hidden",
            scrollbarWidth: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
            msOverflowStyle: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
            "&::-webkit-scrollbar": {
              width: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" },
              height: { xs: 0, sm: 0, md: "8px", lg: "8px", xl: "8px" },
            },
          }}
        >
          {/* Editable Name */}
          <TextField
            label="Name"
            value={editedName}
            onChange={handleNameChange}
            fullWidth
            disabled={!editProfilePermission}
            sx={textFieldStyles}
          />

          {/* Email */}
          <TextField
            label="Email"
            type="email"
            value={userDetails?.email || ""}
            fullWidth
            disabled
            sx={textFieldStyles}
          />

          {/* Status */}
          <FormControl fullWidth>
            <TextField
              label="Status"
              value={userDetails?.status || ""}
              fullWidth
              disabled
              sx={textFieldStyles}
            />
          </FormControl>

          {/* Created At */}
          <TextField
            label="Created At"
            value={userDetails?.createdAt || ""}
            fullWidth
            disabled
            sx={textFieldStyles}
          />

          {/* Role */}
          <FormControl fullWidth>
            <TextField
              label="Role"
              value={userDetails?.Roles?.roleName || ""}
              fullWidth
              disabled
              sx={textFieldStyles}
            />
          </FormControl>

          {/* Save Button */}
          <Tooltip
            title={
              !editProfilePermission
                ? "You don't have permission to edit your profile"
                : ""
            }
          >
            <span style={{ alignSelf: "flex-start" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={updateLoading || !editProfilePermission}
                sx={{
                  marginTop: "2rem",
                  width: "fit-content", // Makes button only as wide as its content
                  paddingX: "2rem", // Optional: Adds some horizontal padding for better appearance
                  "&.Mui-disabled": {
                    backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                    color: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.38)",
                  },
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                {updateLoading ? "Saving..." : "Save Changes"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Snackbar for success and error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1, // Ensure Snackbar is above the modal
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
